import React, { useState, useEffect, useCallback } from 'react';
import { UserCheck, UserX, Loader2, Mail, Calendar } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from 'sonner';

export const AdminPendingApprovalsTab = ({ api }) => {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actioningId, setActioningId] = useState(null);

  const fetchPending = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/pending-users');
      setPending(res.data);
    } catch (error) {
      console.error('Failed to fetch pending users:', error);
      toast.error('Could not load pending accounts.');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  const handleApprove = async (userId, name) => {
    if (!window.confirm(`Approve ${name}? They'll get a welcome email and can log in immediately.`)) return;
    setActioningId(userId);
    try {
      await api.post('/admin/approve-user', { user_id: userId, send_welcome_email: true });
      toast.success(`${name} has been approved.`);
      setPending(prev => prev.filter(u => u.id !== userId));
    } catch (error) {
      console.error('Approve failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve user.');
    } finally {
      setActioningId(null);
    }
  };

  const handleReject = async (userId, name) => {
    if (!window.confirm(`Reject ${name}? They will not be able to log in. You can reverse this later if needed.`)) return;
    setActioningId(userId);
    try {
      await api.post('/admin/reject-user', { user_id: userId });
      toast.success(`${name} has been rejected.`);
      setPending(prev => prev.filter(u => u.id !== userId));
    } catch (error) {
      console.error('Reject failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to reject user.');
    } finally {
      setActioningId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="pending-approvals-loading">
        <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="pending-approvals-tab">
      <Card className="border-brand-primary/10">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <UserCheck className="w-5 h-5 text-brand-accent" />
            Pending Approvals ({pending.length})
          </CardTitle>
          <p className="text-sm text-text-muted mt-1">
            These writers verified their email and are waiting for you to approve them. Approve real writers. Reject anything that looks like a bot.
          </p>
        </CardHeader>
        <CardContent>
          {pending.length === 0 ? (
            <p className="text-text-muted text-sm text-center py-8" data-testid="no-pending-users">
              No pending approvals right now. 🎉
            </p>
          ) : (
            <div className="space-y-3">
              {pending.map(u => (
                <div
                  key={u.id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-surface-alt rounded-lg border border-brand-primary/10"
                  data-testid={`pending-user-${u.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-text-primary">{u.display_name || u.username}</p>
                    <p className="text-sm text-text-secondary">@{u.username}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-text-muted">
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" /> {u.email}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> {new Date(u.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    {u.bio && (
                      <p className="text-xs text-text-secondary mt-2 italic line-clamp-2">"{u.bio}"</p>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      onClick={() => handleApprove(u.id, u.display_name || u.username)}
                      disabled={actioningId === u.id}
                      size="sm"
                      className="btn-accent gap-1"
                      data-testid={`approve-user-${u.id}`}
                    >
                      {actioningId === u.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserCheck className="w-3 h-3" />}
                      Approve
                    </Button>
                    <Button
                      onClick={() => handleReject(u.id, u.display_name || u.username)}
                      disabled={actioningId === u.id}
                      size="sm"
                      variant="outline"
                      className="gap-1 border-red-300 text-red-600 hover:bg-red-50"
                      data-testid={`reject-user-${u.id}`}
                    >
                      <UserX className="w-3 h-3" />
                      Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
